import { ListResponse } from "store/api/types";
import { loadStoreValue, STORAGE_KEY_TOKEN } from "./session-util";

/** The CSRF cookie the API itself sets, domain-scoped to `.caltana.com`. */
const CSRF_COOKIE_NAME = "csrftoken";

/**
 * The CSRF token web-next stores for itself from the login response, under its
 * own name so it cannot shadow the API's cookie in `document.cookie`. Must stay
 * in sync with `CLIENT_CSRF_COOKIE_NAME` in web-next's `src/lib/auth-client.ts`.
 *
 * Both apps are served from the same origin (legacy routes are proxied through
 * web-next's host by its fallback rewrite), so they share one cookie jar and
 * have to agree on which token to send.
 */
export const CLIENT_CSRF_COOKIE_NAME = "caltana_client_csrftoken";

const readCookie = (cookieName: string) => {
  if (typeof window === "undefined" || !document.cookie) {
    return "";
  }

  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const parts = cookie.split("=");
    if (parts.length >= 2) {
      const name = parts[0].trim();
      if (name === cookieName) {
        // Join the rest of the parts in case the value itself contained '='
        const value = parts.slice(1).join("=").trim();
        return value;
      }
    }
  }

  return "";
};

/**
 * The CSRF token to send in `X-CSRFToken`.
 *
 * Prefers the API's own cookie, which is the value the server compares against.
 * The client-stored copy is the fallback for hosts where the API's cookie is
 * cross-site and therefore unreadable — a Vercel preview against
 * dev-api.caltana.com, where it is the only token available to this origin.
 */
export const getCsrfToken = () =>
  readCookie(CSRF_COOKIE_NAME) || readCookie(CLIENT_CSRF_COOKIE_NAME);

/**
 * Get the auth token from cookies (client-side only)
 * This is needed for cross-origin API requests where cookies may not be sent automatically
 */
export const getAuthToken = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const storedToken = loadStoreValue(STORAGE_KEY_TOKEN);
  if (typeof storedToken === "string" && storedToken.trim()) {
    return storedToken;
  }

  return readCookie("auth_token");
};

/**
 * Check if the API URL is cross-origin (different domain than the current page)
 * This is used to determine if we need to send the Authorization header explicitly
 */
export const isCrossOriginApi = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const apiUrl = process.env.NEXT_PUBLIC_HOST_URL || "";
  if (!apiUrl) {
    return false;
  }

  try {
    const apiOrigin = new URL(apiUrl).origin;
    const currentOrigin = window.location.origin;
    return apiOrigin !== currentOrigin;
  } catch {
    return false;
  }
};

const formatFieldName = (field: string): string => {
  // Convert snake_case to human-readable format
  const formatted = field.replace(/_/g, " ");

  // Capitalize first letter of each word
  return formatted.replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDateErrorMessage = (field: string, message: string): string => {
  const fieldName = formatFieldName(field);

  // Handle specific date validation errors with more user-friendly messages
  if (message.includes("should not be in the past")) {
    return `${fieldName} cannot be set to a past date. Please select a future date.`;
  }

  if (message.includes("invalid date")) {
    return `${fieldName} has an invalid date format. Please check the date and try again.`;
  }

  if (message.includes("required")) {
    return `${fieldName} is required. Please provide a valid date.`;
  }

  // Return formatted message for other cases
  return `${fieldName}: ${message}`;
};

export const getErrorMessage = (
  actionName: string,
  response: Record<string, unknown>,
): string => {
  // Check if response has data property that could contain structured error messages
  if (response && typeof response === "object") {
    // Check if the response follows the pattern of field-specific error messages
    const errorEntries = Object.entries(response);
    if (errorEntries.length > 0) {
      // Check if entries have array values with string messages
      const hasStructuredErrors = errorEntries.every(
        ([_, value]) =>
          Array.isArray(value) &&
          value.length > 0 &&
          typeof value[0] === "string",
      );

      if (hasStructuredErrors) {
        // Format field-specific error messages with improved date error handling
        return errorEntries
          .map(([field, messages]) => {
            const messageArray = Array.isArray(messages)
              ? messages
              : [messages];
            const message = messageArray.join(", ");

            // Check if this is a date-related field and format accordingly
            if (
              field.includes("date") ||
              field.includes("due") ||
              field.includes("decision")
            ) {
              return formatDateErrorMessage(field, message);
            }

            // Default formatting for non-date fields
            const fieldName = formatFieldName(field);
            return `${fieldName}: ${message}`;
          })
          .join("\n");
      }
    }
  }

  // Default generic error message
  return `Error in ${actionName}!`;
};

export const transformPaginatedResponse = <T>(
  response: ListResponse<T> | T[],
) => ("results" in response ? response?.results : response);

/**
 * Specialized error message handler for supplier notification requests
 * Handles date validation errors with user-friendly messages
 */
export const getSupplierNotificationErrorMessage = (
  response: Record<string, unknown>,
): string => {
  if (response && typeof response === "object") {
    const errorEntries = Object.entries(response);

    if (errorEntries.length > 0) {
      const hasStructuredErrors = errorEntries.every(
        ([_, value]) =>
          Array.isArray(value) &&
          value.length > 0 &&
          typeof value[0] === "string",
      );

      if (hasStructuredErrors) {
        const errorMessages = errorEntries.map(([field, messages]) => {
          const messageArray = Array.isArray(messages) ? messages : [messages];
          const message = messageArray.join(", ");

          // Handle specific date validation errors for supplier notifications
          if (field === "response_decision_date") {
            if (message.includes("should not be in the past")) {
              return "Response Decision Date cannot be set to a past date. Please select a future date.";
            }
            return `Response Decision Date: ${message}`;
          }

          if (field === "response_due_date") {
            if (message.includes("should not be in the past")) {
              return "Response Due Date cannot be set to a past date. Please select a future date.";
            }
            return `Response Due Date: ${message}`;
          }

          // Handle other date fields
          if (field.includes("date")) {
            return formatDateErrorMessage(field, message);
          }

          // Default formatting for non-date fields
          const fieldName = formatFieldName(field);
          return `${fieldName}: ${message}`;
        });

        return errorMessages.join("\n");
      }
    }
  }

  return "Failed to send quote request. Please check the form and try again.";
};
